/**
 * The one countdown module (JUB-176). Every "how long until the event" number
 * on the platform comes from here: the public templates' ticking countdown
 * (golden_serenity and botez_bokeh TSX, which retired theme keys fall back to,
 * and the golden_serenity template-engine package via
 * `lib/template-engine/context.ts`), the dashboard event card and the
 * onboarding wizard. `pages/public/templates/shared/countdown.ts` re-exports
 * it for the template code.
 *
 * It answers two different questions, each with exactly one rule:
 *
 * 1. Time left until the event instant — `computeCountdown`,
 *    `countdownParts`, `calculateCountdown`. The remaining duration split into
 *    whole 24-hour periods ("days") plus hours, minutes and seconds, every unit
 *    floored. The four numerals sit side by side and tick, so they must add up
 *    to the true remaining time: DAYS is floor(remaining / 24 h), never a
 *    calendar count. A bare "YYYY-MM-DD" is read by `new Date()` as UTC
 *    midnight (ECMA-262), so the instant, and therefore the countdown, is the
 *    same for every viewer. Past, missing or unparseable dates have no parts:
 *    never a negative number.
 *
 * 2. Calendar days until the event date — `calendarDaysUntil`. A date, not a
 *    duration: the event's calendar day minus today's calendar day, both read
 *    in the event's zone, so an event tomorrow is 1 at any hour of today. This
 *    is the dashboard card's "zile până la eveniment" and the wizard's
 *    "Mai sunt N zile".
 *
 * Why not one rule for both: calendar days on the ticking clock would read
 * "9 days 17 hours" when 8 days 17 hours remain, and 24-hour periods on the
 * card would read "0 days" on the evening before the wedding. For the same
 * event at the same moment the two never drift apart: while the countdown
 * runs, `calendarDaysUntil` equals the countdown's DAYS, or DAYS + 1 once the
 * time of day has passed the event's own time of day (the unit tests sweep
 * this across both DST changes).
 */

export interface CountdownValue {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export type CountdownUnit = "days" | "hours" | "minutes" | "seconds";

/** Display order of the countdown units, largest first. */
export const COUNTDOWN_UNITS: readonly CountdownUnit[] = ["days", "hours", "minutes", "seconds"];

/** One countdown unit ready to render: its value and the i18n key of its label. */
export interface CountdownPartValue {
  unit: CountdownUnit;
  value: number;
  labelKey: string;
}

const SECOND_MS = 1000;
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

const ZERO_COUNTDOWN: CountdownValue = { days: 0, hours: 0, minutes: 0, seconds: 0 };

/**
 * Time left until `target`, as whole 24-hour periods plus hours, minutes and
 * seconds (rule 1 in the module docs). Every unit is floored, never rounded
 * up. Null once the instant has passed, or when `target` is missing or
 * unparseable.
 */
export function computeCountdown(target: string | null | undefined, now: Date = new Date()): CountdownValue | null {
  if (!target) return null;
  const distance = new Date(target).getTime() - now.getTime();
  if (!Number.isFinite(distance) || distance < 0) return null;
  return {
    days: Math.floor(distance / DAY_MS),
    hours: Math.floor((distance % DAY_MS) / HOUR_MS),
    minutes: Math.floor((distance % HOUR_MS) / MINUTE_MS),
    seconds: Math.floor((distance % MINUTE_MS) / SECOND_MS),
  };
}

/**
 * `computeCountdown` with all-zero in place of null, for the TSX templates
 * whose layout keeps the four "00" tiles once the date has passed.
 */
export const calculateCountdown = (date: string | undefined | null, now: Date = new Date()): CountdownValue =>
  computeCountdown(date, now) ?? { ...ZERO_COUNTDOWN };

/** `events.form.template.<key>` used when the unit's value is exactly 1. */
const SINGULAR_I18N_KEY: Record<CountdownUnit, string> = {
  days: "events.form.template.day",
  hours: "events.form.template.hour",
  minutes: "events.form.template.minute",
  seconds: "events.form.template.second",
};

/** `events.form.template.<key>` used when the unit's value is 0 or >= 2. */
const PLURAL_I18N_KEY: Record<CountdownUnit, string> = {
  days: "events.form.template.days",
  hours: "events.form.template.hours",
  minutes: "events.form.template.minutes",
  seconds: "events.form.template.seconds",
};

/**
 * i18n key for a countdown unit's label, RO/EN-grammatically-correct for
 * singular vs. plural (JUB-94). RO nouns used here ("zi/zile", "oră/ore",
 * "minut/minute", "secundă/secunde") take the same plural form for both the
 * CLDR "few" (2-19) and "other" (20+) categories, so a plain one/other split
 * on `value === 1` is grammatically complete — no `Intl.PluralRules` "few"
 * category is needed.
 *
 * Resolve the returned key with a plain `t(key)` call, no `{ count }`
 * param: the golden_serenity template-engine package's translator
 * (`messageTranslator.ts`) is a flat key lookup with no i18next plural-
 * suffix support, so the key itself must already be singular/plural-correct
 * for both the live app (react-i18next) and that package to render — and
 * stay in parity — the same way.
 */
export const countdownUnitLabelKey = (unit: CountdownUnit, value: number): string =>
  value === 1 ? SINGULAR_I18N_KEY[unit] : PLURAL_I18N_KEY[unit];

/**
 * The countdown as an ordered list of units with their label keys; empty
 * once the event instant has passed, or when `target` is missing or
 * unparseable.
 */
export function countdownParts(target: string | null | undefined, now: Date = new Date()): CountdownPartValue[] {
  const countdown = computeCountdown(target, now);
  if (!countdown) return [];
  return COUNTDOWN_UNITS.map((unit) => ({
    unit,
    value: countdown[unit],
    labelKey: countdownUnitLabelKey(unit, countdown[unit]),
  }));
}

/**
 * The zone event dates are planned in. The API sends an event date as a bare
 * calendar day with no zone; the platform serves Romanian events and its
 * backend runs on this zone (`server/config/app.php`), so "today" is read
 * here rather than in the viewer's browser zone, which keeps the day count
 * the same for an owner or guest abroad.
 */
export const EVENT_TIME_ZONE = "Europe/Bucharest";

/** Leading "YYYY-MM-DD", optionally followed by a time ("T..." or " ..."). */
const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/;

const dayFormatters = new Map<string, Intl.DateTimeFormat>();

/** Today's calendar day in `timeZone`, as the UTC midnight of that day. */
function calendarDayIn(now: Date, timeZone: string): number | null {
  if (!Number.isFinite(now.getTime())) return null;
  let formatter = dayFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric" });
    dayFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((entry) => entry.type === type)?.value);
  return Date.UTC(part("year"), part("month") - 1, part("day"));
}

/**
 * Whole calendar days from today to the event's calendar date (rule 2 in the
 * module docs): 0 on the event day, 1 the day before, negative once the date
 * has passed (display callers clamp; the wizard's past-date warning needs the
 * sign). The event's Y/M/D digits are taken verbatim, never shifted through
 * UTC or an offset suffix; today is the calendar day of `now` in `timeZone`.
 * Both days are compared as UTC midnights, so a DST change between them
 * cannot add or lose an hour and the result is an exact integer. Null when
 * the date is missing, malformed or impossible ("2027-02-30").
 */
export function calendarDaysUntil(
  date: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = EVENT_TIME_ZONE,
): number | null {
  const match = date ? CALENDAR_DATE_PATTERN.exec(date) : null;
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const eventDay = Date.UTC(year, month - 1, day);
  const check = new Date(eventDay);
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  const today = calendarDayIn(now, timeZone);
  if (today === null) return null;
  return (eventDay - today) / DAY_MS;
}
